<template>
    <div>
        <!-- Preloaders -->
        <PagePreloader v-if="isSearching"></PagePreloader>

        <!-- Error management -->
        <CustomAlerts :errors="errors"></CustomAlerts>

        <div class="row">
            <div class="col-xl-12">
                <div class="card card-custom gutter-b">
                    <div class="card-header">
                        <div class="card-title">
                            <span class="card-icon">
                                <i class="fas fa-hashtag text-success"></i>
                            </span>
                            <h3 class="card-label">Bulk Search</h3>
                        </div>
                    </div>
                    <div class="card-body">
                        <form
                            method="POST"
                            ref="hashtagSearchForm"
                            v-on:submit.prevent="doBulkSearch"
                        >
                            <!-- Hashtag input -->
                            <b-form-group label-cols="4" label-cols-lg="2" label="Hashtags">
                                <b-form-textarea
                                    id="hashtags"
                                    name="hashtags"
                                    placeholder="Enter up to 200 hashtags here..."
                                    rows="3"
                                    max-rows="6"
                                    :value="textareaContent"
                                ></b-form-textarea>
                            </b-form-group>

                            <!-- Number of likes -->
                            <b-form-group
                                label-cols="4"
                                label-cols-lg="2"
                                label="Your average number of likes per post"
                            >
                                <b-form-input
                                    id="likes"
                                    name="likes"
                                    type="number"
                                    placeholder="Optional - Enter your number of likes here"
                                    class="w-100"
                                ></b-form-input>
                            </b-form-group>

                            <!-- Submit -->
                            <input
                                type="Submit"
                                @click="doBulkSearch"
                                class="d-none"
                                :disabled="isSearching"
                            />
                        </form>
                    </div>
                    <div class="card-footer">
                        <b-button
                            variant="success"
                            class="offset-xl-2 col-xl-3"
                            @click="doBulkSearch"
                            :disabled="isSearching"
                        >
                            Search
                        </b-button>
                    </div>
                </div>
                <!-- End Search Card -->

                <!-- Display search results -->
                <div v-if="displayResults">
                    <!-- Copy hashtags section -->
                    <div class="card card-custom gutter-b">
                        <div class="card-header">
                            <div class="card-title">
                                <span class="card-icon">
                                    <i class="fas fa-hashtag text-success"></i>
                                </span>
                                <h3 class="card-label">Selected Hashtags</h3>
                            </div>
                            <div class="card-toolbar">
                                <span
                                    class="btn btn-bold btn-sm"
                                    :class="
                                        selectedHashtags.length >= 30
                                            ? 'btn-outline-danger'
                                            : 'btn-outline-primary'
                                    "
                                >
                                    {{ selectedHashtags.length }} hashtags selected
                                </span>
                            </div>
                        </div>
                        <div class="card-body">
                            <div v-if="selectedHashtags.length < 1">
                                <infoBox
                                    text="You currently haven't selected any hashtags. Use the tables below to make your selection."
                                ></infoBox>
                            </div>

                            <div v-if="selectedHashtags.length > 0">
                                <div class="row">
                                    <div class="col-xl-12 btn-group" style="flex-wrap: wrap">
                                        <div v-for="selectedHashtag in selectedHashtags">
                                            <span
                                                class="
                                                    btn btn-sm btn-font-sm
                                                    font-weight-bold
                                                    btn-light-primary
                                                    m-1
                                                "
                                                >#{{ selectedHashtag }}</span
                                            >
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="card-footer row" v-if="selectedHashtags.length > 0">
                            <!-- NOTE - Copy to Clipboard button -->
                            <buttonCopyToClipboard
                                :propHashtags="selectedHashtagsHashed"
                                class="col-md-3 col-12"
                            ></buttonCopyToClipboard>
                            <!-- NOTE - Create new hashtag set -->
                            <buttonCreateNewHashtagSet
                                :propHashtags="selectedHashtagsHashed"
                                @emitPassMessage="handleEmitMessages"
                                class="col-md-3 col-12"
                            >
                            </buttonCreateNewHashtagSet>
                            <!-- NOTE - Add to existing hashtag set -->
                            <buttonAddToExistingHashtagSet
                                @emitPassMessage="handleEmitMessages"
                                :propHashtags="selectedHashtagsHashed"
                                class="col-md-3 col-12"
                            ></buttonAddToExistingHashtagSet>
                        </div>
                    </div>
                    <!-- End Copy Hashtags -->

                    <!-- Search results section -->
                    <div class="card card-custom gutter-b">
                        <div class="card-header">
                            <div class="card-title">
                                <span class="card-icon">
                                    <i class="fas fa-hashtag text-success"></i>
                                </span>
                                <h3 class="card-label">Results</h3>
                            </div>
                            <div class="card-toolbar">
                                <div class="btn-group">
                                    <b-button variant="light-primary" size="sm" @click="selectAll()"
                                        >Select All</b-button
                                    >
                                    <b-button
                                        variant="light-danger"
                                        size="sm"
                                        @click="deselectAll()"
                                        >Deselect All</b-button
                                    >
                                </div>
                            </div>
                        </div>

                        <div class="card-body">
                            <!-- Likes indicator -->
                            <div v-if="hasLikes">
                                <LikesLegend></LikesLegend>
                            </div>

                            <div class="form-inline gutter-b">
                                <label class="mr-sm-2" for="inline-form-custom-select-pref"
                                    >Hashtags per page</label
                                >
                                <b-form-select
                                    class="numPerPage"
                                    v-model="perPage"
                                    :options="pageOptions"
                                ></b-form-select>
                            </div>

                            <!-- Results -->
                            <b-table
                                striped
                                hover
                                selectable
                                bordered
                                select-mode="multi"
                                @row-clicked="(item) => onRowClicked('bulk', item)"
                                :fields="fields"
                                :items="searchResults"
                                :per-page="perPage"
                                :current-page="currentpage"
                                sortBy="count"
                                :sortDesc="true"
                                :sort-compare="customTableSort"
                                responsive="sm"
                                ref="selectableTable"
                            >
                                <!-- Checkbox slot -->
                                <template v-slot:cell(selected)="{ item, field: { key } }">
                                    <template v-if="item[key]">
                                        <span aria-hidden="true"
                                            ><i
                                                class="
                                                    far
                                                    fa-check-square
                                                    text-primary
                                                    font-weight-bolder
                                                "
                                            ></i
                                        ></span>
                                        <span class="sr-only">Selected</span>
                                    </template>
                                    <template v-else>
                                        <span aria-hidden="true"
                                            ><i class="far fa-square"></i
                                        ></span>
                                        <span class="sr-only">Not selected</span>
                                    </template>
                                </template>

                                <!-- Hashtag badge slot -->
                                <template v-slot:cell(hashtagsBadged)="{ item, field: { key } }">
                                    {{ item.hashtag }}
                                    <span
                                        class="badge badge-warning"
                                        v-if="item.status == 'flagged'"
                                        >Flagged</span
                                    >
                                    <span class="badge badge-danger" v-if="item.status == 'banned'"
                                        >Banned</span
                                    >
                                </template>

                                <!-- Detailed analytics slot -->
                                <template v-slot:cell(customButtons)="{ item, field: { key } }">
                                    <buttonGetHashtagStatistics
                                        :propHashtag="item.hashtag"
                                    ></buttonGetHashtagStatistics>
                                </template>
                            </b-table>
                            <b-pagination
                                v-model="currentpage"
                                :total-rows="searchResults.length"
                                :per-page="perPage"
                                align="center"
                                aria-controls="my-table"
                                first-number
                                last-number
                            >
                            </b-pagination>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import "~bootstrap-vue/dist/bootstrap-vue.css";
</style>

<script>
// Defaults
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import axios from "axios";
// Components
import PagePreloader from "@/view/content/Custom/PagePreloader.vue";
import CustomAlerts from "@/view/content/Custom/CustomAlerts.vue";
import InfoBox from "@/view/content/Custom/InfoBox.vue";
import LikesLegend from "@/view/content/Custom/LikesLegend.vue";
// Buttons
import buttonAddToExistingHashtagSet from "@/view/content/Custom/buttonAddToExistingHashtagSet.vue";
import buttonCreateNewHashtagSet from "@/view/content/Custom/buttonCreateNewHashtagSet.vue";
import buttonCopyToClipboard from "@/view/content/Custom/buttonCopyToClipboard.vue";
import buttonGetHashtagStatistics from "@/view/content/Custom/buttonGetHashtagStatistics.vue";
// Mixins
import stringManagement from "@/mixins/stringManagement";
import hashtagTableManagement from "@/mixins/hashtagTableManagement";
import axiosErrorManagement from "@/mixins/axiosErrorManagement";

export default {
    name: "BulkPage",
    mixins: [stringManagement, hashtagTableManagement, axiosErrorManagement],
    components: {
        PagePreloader,
        InfoBox,
        CustomAlerts,
        LikesLegend,
        buttonAddToExistingHashtagSet,
        buttonCreateNewHashtagSet,
        buttonCopyToClipboard,
        buttonGetHashtagStatistics,
    },
    props: ["propHashtags"],
    data() {
        return {
            // Preloaders
            pageIsLoaded: false,
            isSearching: false,
            modalisloading: false,

            // Displayers
            displayResults: false,
            hasLikes: false,

            // Error management
            errors: [],
            modalerrors: [],

            // Data
            searchResults: [],
            selectedHashtags: [],
            textareaContent: "",

            // Table management
            perPage: 15,
            pageOptions: [
                5,
                15,
                25,
                50,
                {
                    value: Number.MAX_SAFE_INTEGER,
                    text: "Show all",
                },
            ],
            currentpage: 1,
            fields: [
                {
                    key: "selected",
                    label: "",
                },
                {
                    key: "hashtag",
                    sortable: true,
                    tdClass: "d-none",
                    thClass: "d-none",
                },
                {
                    key: "hashtagsBadged",
                    sortable: true,
                    label: "Hashtags",
                },
                {
                    key: "count",
                    sortable: true,
                    label: "Post count",
                    formatter: (value) => {
                        if (typeof value === "undefined" || value === null) {
                            return 0;
                        }
                        return this.addCommaToNumber(value);
                    },
                },
                {
                    key: "DAPC_weekly",
                    sortable: true,
                    label: "Daily Posts",
                    formatter: (value) => {
                        if (typeof value === "undefined" || value === null) {
                            return 0;
                        }
                        return this.addCommaToNumber(Math.round(value));
                    },
                },
                {
                    key: "score",
                    sortable: true,
                    label: "Score",
                },
                {
                    key: "avgMaxLikes",
                    sortable: true,
                    label: "Max Likes in Top 9",
                    formatter: (value) => {
                        if (typeof value === "undefined" || value === null) {
                            return 0;
                        }
                        return this.addCommaToNumber(value);
                    },
                },
                {
                    key: "avgMinLikes",
                    sortable: true,
                    label: "Min Likes in Top 9",
                    formatter: (value) => {
                        if (typeof value === "undefined" || value === null) {
                            return 0;
                        }
                        return this.addCommaToNumber(value);
                    },
                },
                {
                    key: "avgAvgLikes",
                    sortable: true,
                    label: "Average Likes in Top 9",
                    formatter: (value) => {
                        if (typeof value === "undefined" || value === null) {
                            return 0;
                        }
                        return this.addCommaToNumber(value);
                    },
                },
                {
                    key: "avgTopPostVelocity",
                    sortable: true,
                    label: "Top Post likes per hour",
                    formatter: (value) => {
                        if (typeof value === "undefined" || value === null) {
                            return 0;
                        }
                        return this.addCommaToNumber(value);
                        // return value.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ",");
                    },
                },
                {
                    key: "avgAvgVelocity",
                    sortable: true,
                    label: "Avg. Top9 likes per hour",
                    formatter: (value) => {
                        if (typeof value === "undefined" || value === null) {
                            return 0;
                        }
                        return this.addCommaToNumber(value);
                        // return value.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ",");
                    },
                },
                {
                    key: "avgMinVelocity",
                    sortable: true,
                    label: "Min. Top9 likes per hour",
                    formatter: (value) => {
                        if (typeof value === "undefined" || value === null) {
                            return 0;
                        }
                        return this.addCommaToNumber(value);
                        // return value.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ",");
                    },
                },
                {
                    key: "customButtons",
                    label: "Options",
                },
            ],
        };
    },

    methods: {
        doBulkSearch() {
            // Reset errors
            this.errors = [];

            // Reset likes
            this.hasLikes = false;

            // Get form
            var form = this.$refs["hashtagSearchForm"];

            // Get values
            var hashtags = form.hashtags.value;
            var likes = form.likes.value;

            // Reset display status
            this.displayResults = false;

            // Preloader
            this.isSearching = true;

            // Perform
            axios
                .post("search/bulk", {
                    hashtags: hashtags,
                    likes: likes,
                })
                .then((response) => {
                    // Make pretty
                    var data = response.data;

                    // Check if we're good to go
                    if (data.status == "OK") {
                        // Display results
                        this.displayResults = true;

                        // Check if we have likes
                        if (data.likes) {
                            // Loop through all results and manage variants
                            data.data.forEach((currentItem, index, theArray) => {
                                // TODO - Check if is defined?
                                // Reset status
                                this.hasLikes = true;

                                // NOTE - Colour the hashtag rows if we need to
                                theArray[index]._rowVariant = this.returnHashtagRowColour(
                                    data.likes,
                                    currentItem
                                );
                                // NOTE - Previous version of the script
                                // Check if we don't have a ZERO
                                // if (currentItem.avgAvgLikes != 0) {
                                //     if (
                                //         data.likes > currentItem.avgMinLikes &&
                                //         data.likes < currentItem.avgAvgLikes * 1.1
                                //     ) {
                                //         theArray[index]._rowVariant = "warning";
                                //     } else if (data.likes <= currentItem.avgMinLikes) {
                                //         theArray[index]._rowVariant = "danger";
                                //     } else {
                                //         theArray[index]._rowVariant = "success";
                                //     }
                                // }
                            });
                        }

                        // Add results to array
                        this.searchResults = Object.values(data.data);
                    }

                    // Do error management
                    if (data.messages.length > 0) {
                        data.messages.forEach((item) => {
                            this.errors.push({ type: item.type, message: item.message });
                        });
                    }
                })
                .catch((error) => {
                    this.errors.push(this.handleAxiosErrors(error));
                })
                .finally(() => {
                    // Remove preloader
                    this.isSearching = false;
                });
        },

        onRowClicked(tableid, item) {
            // Hack
            if (item.selected) {
                this.$set(item, "selected", false);
            } else {
                this.$set(item, "selected", true);
            }
            // Re-run the selection grabber
            this.getAllSelectedItems();
        },

        getAllSelectedItems() {
            // Get variable to store items
            var hSelection = [];

            // Generate array of all selected hashtags by table
            // Will this not use up a lot of memory?
            this.searchResults.forEach((item) => {
                if (item.selected == true) {
                    hSelection.push(item.hashtag);
                }
            });

            // Remove duplicates
            hSelection = [...new Set(hSelection)];

            // Return variable
            this.selectedHashtags = hSelection;
        },

        selectAll() {
            // Loop
            this.searchResults.forEach((part, index, theArray) => {
                this.$set(this.searchResults[index], "selected", true);
            });
            // Re-run the selection grabber
            this.getAllSelectedItems();
        },

        deselectAll() {
            // Loop
            this.searchResults.forEach((part, index, theArray) => {
                this.$set(this.searchResults[index], "selected", false);
            });
            // Re-run the selection grabber
            this.getAllSelectedItems();
        },

        handleProp() {
            if (typeof this.propHashtags !== "undefined") {
                // Set data in the form
                var form = this.$refs["hashtagSearchForm"];
                form.hashtags.value = this.propHashtags;
                // Fill in the text area
                this.textareaContent = this.propHashtags;

                // Submit form
                this.doBulkSearch();
            }
        },

        handleEmitMessages(messages) {
            if (messages.length > 0) {
                messages.forEach((currentItem) => {
                    this.errors.push({ type: currentItem.type, message: currentItem.message });
                });
            }
        },
    },

    computed: {
        selectedHashtagsHashed() {
            return "#" + this.selectedHashtags.join(" #");
        },
    },

    mounted() {
        // Handle props
        this.handleProp();

        // Set breadcrumbs
        this.$store.dispatch(SET_BREADCRUMB, [
            { title: "Search", route: "search" },
            { title: "Bulk Search", route: "bulk" },
        ]);
    },
};
</script>
